import { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { t } from "../../translations";

import "../styles/section-chef.css";

import { CircularProgress } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { POST_USER_DATA } from "../../store/action";
import { createLogger } from "../../utils/logger";
import { GET, UPDATE } from "../constants/request-type";
import { service_api } from "../constants/url";
import { MakeRequestAsync } from "../functions/axios";
import { decryptSingleData } from "../functions/cryptojs";
import HubCard from "./hub_card";
import HubRequestForm from "./hub_request_form";
import { openNotificationWithIcon } from "./notification-bar";

const logger = createLogger("SelectHub");

const SelectHub = (props) => {
  const { user } = props;
  const history = useHistory();

  const [hubList, setHubList] = useState([]);
  const [noHubsNearby, setNoHubsNearby] = useState(false);
  const [_, setHubConfig] = useState(null);
  const [showHubRequestForm, setShowHubRequestForm] = useState(false);
  const [selectedHub, setSelectedHub] = useState(null);
  const [load, setLoad] = useState(false);

  const dispatch = useDispatch();
  const lang = useSelector((state) => state.lang);

  const post_user_data = (data) => {
    dispatch(post_user(data));
  };

  useEffect(() => {
    initialData();
  }, []);

  const initialData = async () => {
    // Check if user has location data (zip, street, or position coordinates)
    if (!user?.zip && !user?.street && !user?.position?.lat) {
      setLoad(false);
      return openNotificationWithIcon(
        "error",
        t("hub_selection.no_location_error", lang)
      );
    }

    setLoad(true);

    // Use zip if available, otherwise use coordinates for hub lookup
    const locationIdentifier = user?.street;

    const request_details_hub = {
      type: GET,
      url: service_api,
      route: `hub/get-chef-hub-by-address/${locationIdentifier}`,
      data: null,
      token: user?.refresh_token
    };

    try {
      const response = await MakeRequestAsync(request_details_hub);
      const decryptedData = decryptSingleData(response.data.hub);
      logger.log("Hub data received:", decryptedData);

      // Handle new hub data structure with distance information
      if (decryptedData.hubs) {
        setHubList(decryptedData.hubs);
        setNoHubsNearby(decryptedData.noHubsNearby || false);
        setHubConfig(decryptedData.config || null);
      } else {
        // Fallback for old data structure
        setHubList(decryptedData);
        setNoHubsNearby(decryptedData.length === 0 || false);
        setHubConfig(null);
      }
    } catch (err) {
      logger.log("We couldn't fetch data: ", err?.response?.data?.message);

      // If no hubs found or error, show no hubs nearby message
      setHubList([]);
      setNoHubsNearby(true);
      setHubConfig(null);
    } finally {
      setLoad(false);
    }
  };

  const handleHubRequestSuccess = (requestData) => {
    logger.log("Hub request submitted successfully:", requestData);
    setShowHubRequestForm(false);
    // Optionally refresh hub data or show success message
  };

  const handleRequestHub = () => {
    setShowHubRequestForm(true);
  };

  // Function 1: Just select the hub for confirmation (no API call)
  const selectHubForConfirmation = (hub) => {
    setSelectedHub(hub);
  };

  // Function 2: Confirm the selection and make API call
  const confirmHubSelection = async () => {
    if (!selectedHub) return;

    setLoad(true);

    const request_details = {
      type: UPDATE,
      url: service_api,
      route: `user/update-user/${user?._id}`,
      data: {
        selectedHub: selectedHub._id
      },
      token: user?.refresh_token
    };

    try {
      const response = await MakeRequestAsync(request_details);
      const decryptedData = decryptSingleData(response.data.user);
      post_user_data({
        ...decryptedData
      });
      openNotificationWithIcon(
        "success",
        t("hub_selection.hub_selected_success", lang)
      );
      return history.push("/chef/iam-a-chef/home");
    } catch (err) {
      logger.log("We couldn't fetch data: ", err?.response?.data?.message);
      openNotificationWithIcon(
        "error",
        t("hub_selection.hub_selection_error", lang)
      );
    } finally {
      setLoad(false);
    }
  };

  // Show loading state while fetching hub data
  if (load && hubList.length === 0 && !noHubsNearby) {
    return (
      <div className="section-chef-menu-right-address">
        <div className="section-chef-menu-right-address-inside">
          <div style={{ textAlign: "center", padding: "40px 20px" }}>
            <CircularProgress
              size={50}
              style={{ color: "#ff114d", marginBottom: "20px" }}
            />
            <p style={{ color: "#636e72", fontSize: "16px" }}>
              {t("hub_selection.finding_hubs", lang)}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Show hub request form if requested
  if (showHubRequestForm) {
    return (
      <div className="section-chef-menu-right-address">
        <div className="section-chef-menu-right-address-inside">
          <HubRequestForm
            user={user}
            lang={lang}
            onSuccess={handleHubRequestSuccess}
          />
        </div>
      </div>
    );
  }

  // Show no hubs nearby page if no hubs are available
  if (noHubsNearby) {
    return (
      <div className="section-chef-menu-right-address">
        <div className="section-chef-menu-right-address-inside">
          <div style={{ textAlign: "center", padding: "40px 20px" }}>
            <h2 style={{ color: "#2d3436", marginBottom: "20px" }}>
              {t("hub_selection.no_hubs_available", lang)}
            </h2>

            <div
              style={{
                backgroundColor: "#fff3cd",
                border: "1px solid #ffeaa7",
                borderRadius: "10px",
                padding: "20px",
                marginBottom: "30px",
                textAlign: "left"
              }}
            >
              <p
                style={{
                  color: "#856404",
                  fontSize: "16px",
                  lineHeight: "1.6",
                  margin: 0
                }}
              >
                {t("hub_selection.no_hubs_apology", lang)}
              </p>
            </div>

            <p
              style={{
                color: "#636e72",
                fontSize: "16px",
                marginBottom: "30px"
              }}
            >
              {t("hub_selection.request_hub_question", lang)}
            </p>

            <button
              style={{
                backgroundColor: "#ff114d",
                color: "white",
                border: "none",
                borderRadius: 5,
                padding: "15px 30px",
                fontSize: 18,
                fontWeight: "bold",
                cursor: "pointer",
                transition: "background-color 0.3s",
                marginRight: "15px"
              }}
              onMouseOver={(e) => (e.target.style.backgroundColor = "#e00e47")}
              onMouseOut={(e) => (e.target.style.backgroundColor = "#ff114d")}
              onClick={handleRequestHub}
            >
              {t("hub_selection.request_new_hub", lang)}
            </button>

            <button
              style={{
                backgroundColor: "transparent",
                color: "#ff114d",
                border: "2px solid #ff114d",
                borderRadius: 5,
                padding: "13px 20px",
                fontSize: 16,
                fontWeight: "bold",
                cursor: "pointer",
                transition: "all 0.3s"
              }}
              onMouseOver={(e) => {
                e.target.style.backgroundColor = "#ff114d";
                e.target.style.color = "white";
              }}
              onMouseOut={(e) => {
                e.target.style.backgroundColor = "transparent";
                e.target.style.color = "#ff114d";
              }}
              onClick={() => history.push("/chef/iam-a-chef/address")}
            >
              {t("hub_selection.update_location", lang)}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="section-chef-menu-right-address">
      <div className="section-chef-menu-right-address-inside">
        <div>
          <p
            style={{
              textAlign: "left",
              display: "flex",
              alignItems: "center",
              gap: 15,
              fontWeight: 600,
              marginTop: 30
            }}
          >
            {t("hub_selection.title", lang)}
          </p>
          <p
            style={{
              textAlign: "left",
              fontSize: 18,
              marginBottom: 20
            }}
          >
            {t("hub_selection.subtitle", lang)}
          </p>

          <div className="section-chef-menu-right-address-inside-box">
            {/* Available Hubs List */}
            <div
              style={{
                width: "100%",
                display: "flex",
                flexDirection: "column",
                gap: 15
              }}
            >
              {hubList?.map((hub, index) => (
                <HubCard
                  key={hub._id || index}
                  hub={hub}
                  isSelected={selectedHub?._id === hub._id}
                  onSelect={() => selectHubForConfirmation(hub)}
                  distanceInfo={hub.distanceInfo}
                  t={t}
                  lang={lang}
                />
              ))}
            </div>

            {/* Request New Hub Button */}
            <div style={{ textAlign: "center", marginTop: "20px" }}>
              <button
                style={{
                  backgroundColor: "transparent",
                  color: "#ff114d",
                  border: "2px solid #ff114d",
                  borderRadius: 5,
                  padding: "10px 20px",
                  fontSize: 16,
                  fontWeight: "bold",
                  cursor: "pointer",
                  transition: "all 0.3s"
                }}
                onMouseOver={(e) => {
                  e.target.style.backgroundColor = "#ff114d";
                  e.target.style.color = "white";
                }}
                onMouseOut={(e) => {
                  e.target.style.backgroundColor = "transparent";
                  e.target.style.color = "#ff114d";
                }}
                onClick={handleRequestHub}
              >
                {t("hub_selection.request_new_hub", lang)}
              </button>
            </div>
          </div>
        </div>

        {/* Confirm Selection Button */}
        {selectedHub && (
          <div
            className="btn-get-started"
            style={{
              backgroundColor: "#ff114d",
              width: "100%",
              marginTop: "20px"
            }}
            onClick={() => {
              if (load) {
                return;
              }
              confirmHubSelection();
            }}
          >
            {!load &&
              (lang === "en"
                ? `Select ${selectedHub.name}`
                : `Sélectionner ${selectedHub.name}`)}
            {load && <CircularProgress size={25} />}
          </div>
        )}
      </div>
    </div>
  );
};

export default SelectHub;

const post_user = (data) => ({
  type: POST_USER_DATA,
  data: data
});
